import {
    setFundingType,
    setInsuranceActiveContractor,
    setLeaseActiveContractor,
    setLeaseType,
    setMissingContractor,
    setMissingContractorName,
    setPostMissingContractor,
    setShowContactorNotice,
    setVehicleUsage,
} from "../vehicleInformationSlice";
import React, { JSX, useCallback, useEffect, useRef } from "react";
import { setDossierLoading, updateDossier } from "../../../app/rootReducer";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useLazyGetContractorsByTypeQuery } from "../../apiData/apiDataSlice";
import ContractorInterface from "../../../common/interfaces/ContractorInterface";
import { setGeneralStep } from "../../helpType/helpTypeSlice";
import { GeneralStepsEnum } from "../../../common/constants/GeneralStepsEnum";
import { PatchStepsEnum } from "../../../common/constants/PatchStepsEnum";
import { unsetAppointment } from "../../appointment/appointmentSlice";
import { useParams } from "react-router-dom";
import { fundingTypeEnum } from "../../../common/constants/fundingTypeEnum";
import { dossierTypeEnum } from "../../../common/constants/dossierTypeEnum";
import { postMissingContractorName } from "../../../common/api/Dossier/postMissingContractorMail";
import FormSelectFuzzy from "../../../common/components/form/FormSelectFuzzy";
import { handleContractorSelect } from "../utils/handleContractorSelect";
import { ContractorNotice } from "./ContractorNotice";
import { ContractorEnum } from "../../../common/constants/ContractorEnum";
import { BovagTermsTypeEnum } from "../../../common/constants/BovagTermsTypeEnum";
import { scheduleScrollToStep } from "../../../common/helpers/scrollToStep";

const VehicleInformationLease = (): JSX.Element | null => {
    const dispatch = useAppDispatch();

    const { fundingType, lease, showContactorNotice, vehicleUsage } =
        useAppSelector((state) => state.vehicleInformation);
    const { dossier, dossierCreating, registrationLink, getParams } =
        useAppSelector((state) => state.root);
    const { missingContractor, missingContractorName, postMissingContractor } =
        useAppSelector((state) => state.vehicleInformation);

    const [trigger, { data: contractors }] = useLazyGetContractorsByTypeQuery();

    const renderCondition =
        dossier?.car_model && dossier?.car_brand && lease && !dossierCreating;

    const leaseOrParticulierRef = useRef<HTMLDivElement>(null);
    const leaseFollowUpRef = useRef<HTMLDivElement>(null);
    const leaseContractorRef = useRef<HTMLDivElement>(null);
    const leaseMissingContractorRef = useRef<HTMLDivElement>(null);

    const { slug } = useParams();

    useEffect(() => {
        if (slug) {
            if (slug === "athlon") {
                dispatch(setFundingType(fundingTypeEnum.Lease));
            }
            if (slug === "unigarant") {
                dispatch(setFundingType(fundingTypeEnum.Particulier));
            }
        }
    }, [slug, dispatch]);

    useEffect(() => {
        if (!renderCondition) return;

        scheduleScrollToStep(() => {
            if (
                missingContractor &&
                !lease.activeContractorId &&
                !showContactorNotice
            ) {
                return (
                    leaseMissingContractorRef.current ??
                    leaseContractorRef.current
                );
            }

            if (
                fundingType === fundingTypeEnum.Lease &&
                vehicleUsage &&
                contractors
            ) {
                return leaseContractorRef.current ?? leaseFollowUpRef.current;
            }

            if (
                fundingType === fundingTypeEnum.Lease &&
                leaseFollowUpRef.current
            ) {
                return leaseFollowUpRef.current;
            }

            return leaseOrParticulierRef.current;
        });
    }, [
        renderCondition,
        fundingType,
        vehicleUsage,
        lease.activeContractorId,
        contractors,
        missingContractor,
        postMissingContractor,
        showContactorNotice,
    ]);

    useEffect(() => {
        dispatch(unsetAppointment());
        if (fundingType) {
            switch (fundingType) {
                case fundingTypeEnum.Lease:
                    dispatch(setGeneralStep(GeneralStepsEnum.Lease));
                    dispatch(
                        updateDossier({
                            data: { lease_car: 1 },
                            method: PatchStepsEnum.PatchLeaseCar,
                        }),
                    ).then((result) => {
                        dispatch(setDossierLoading(true));
                        trigger({
                            type: "lease",
                            dossierId: result.payload.dossier_id,
                            preferredEstablishmentId:
                                getParams?.establishmentId,
                            preferredEstablishmentChainId:
                                getParams?.establishmentChainId,
                        }).then(() => {
                            dispatch(setDossierLoading(false));
                        });
                    });
                    break;
                case fundingTypeEnum.Particulier:
                    dispatch(setGeneralStep(GeneralStepsEnum.Lease));
                    dispatch(
                        updateDossier({
                            data: { lease_car: 0 },
                            method: PatchStepsEnum.PatchLeaseCar,
                        }),
                    ).then((result) => {
                        dispatch(setDossierLoading(true));
                        trigger({
                            type: "insurance",
                            dossierId: result.payload.dossier_id,
                            preferredEstablishmentId:
                                getParams?.establishmentId,
                            preferredEstablishmentChainId:
                                getParams?.establishmentChainId,
                        }).then(() => {
                            dispatch(setDossierLoading(false));
                            dispatch(
                                setGeneralStep(GeneralStepsEnum.Insurance),
                            );
                        });
                    });
                    break;
                case fundingTypeEnum.Self:
                    dispatch(setGeneralStep(GeneralStepsEnum.Lease));
                    dispatch(
                        updateDossier({
                            data: { lease_car: 0 },
                            method: PatchStepsEnum.PatchLeaseCar,
                        }),
                    ).then(() => {
                        dispatch(
                            updateDossier({
                                data: { contractor_id: null },
                                method: PatchStepsEnum.PatchContractor,
                            }),
                        );
                        dispatch(setGeneralStep(GeneralStepsEnum.Appointment));
                    });
                    break;
            }

            dispatch(unsetAppointment());
        }
    }, [
        fundingType,
        dispatch,
        trigger,
        getParams?.establishmentChainId,
        getParams?.establishmentId,
    ]);

    const handleFundingTypeClick = useCallback(
        (type: string): void => {
            dispatch(setFundingType(type));
            dispatch(setMissingContractor(false));
            dispatch(setPostMissingContractor(false));
            dispatch(unsetAppointment());
        },
        [dispatch],
    );

    useEffect(() => {
        if (
            !registrationLink.private_lease &&
            !registrationLink.business_lease
        ) {
            handleFundingTypeClick(fundingTypeEnum.Particulier);
        }
    }, [
        registrationLink.private_lease,
        registrationLink.business_lease,
        handleFundingTypeClick,
    ]);

    const handleLeaseTypeClick = useCallback(
        (type: string): void => {
            if (type !== undefined && fundingType === fundingTypeEnum.Lease) {
                dispatch(setGeneralStep(GeneralStepsEnum.Lease));
                dispatch(unsetAppointment());
                dispatch(
                    updateDossier({
                        data: { lease_type_code: type },
                        method: PatchStepsEnum.PatchLeaseTypeCode,
                    }),
                ).then(() => {
                    dispatch(setGeneralStep(GeneralStepsEnum.Appointment));
                });
            }
        },
        [dispatch, fundingType],
    );

    const handleContinueWithoutContractor = (): void => {
        dispatch(setMissingContractor(true));
    };

    const handleMissingContractorNameChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        dispatch(setMissingContractorName(event.target.value));
    };

    const handleMissingContractorSubmit = (): void => {
        const localDossierUuidKey = slug + "-dossier_uuid";
        const localDossierAccessTokenKey = slug + "-dossier_access_token";
        const uuid = localStorage.getItem(localDossierUuidKey) + "";
        const accessToken =
            localStorage.getItem(localDossierAccessTokenKey) + "";

        if (!postMissingContractor) {
            dispatch(setPostMissingContractor(true));
            dispatch(
                postMissingContractorName({
                    name: missingContractorName,
                    uuid,
                    accessToken,
                    dossierId: dossier?.dossier_id ?? 0,
                }),
            ).then(() => {
                dispatch(setGeneralStep(GeneralStepsEnum.Appointment));
            });
        }
    };

    const handleVehicleUsageChange = (usage: BovagTermsTypeEnum): void => {
        dispatch(setVehicleUsage(usage));

        if (fundingType === fundingTypeEnum.Lease) {
            const leaseType =
                usage === BovagTermsTypeEnum.BUSINESS ? "business" : "private";
            dispatch(setLeaseType(leaseType));

            dispatch(
                updateDossier({
                    data: {
                        bovag_terms_type: usage,
                        lease_type_code: leaseType,
                    },
                    method: PatchStepsEnum.PatchBovagTermsType,
                }),
            ).then(() => {
                dispatch(setGeneralStep(GeneralStepsEnum.Appointment));
            });
        } else {
            dispatch(
                updateDossier({
                    data: { bovag_terms_type: usage },
                    method: PatchStepsEnum.PatchBovagTermsType,
                }),
            );
        }
    };

    const fuzzyOptions = {
        keys: [{ name: "label", weight: 1.0 }],
        valueKey: "label",
        maxPatternLength: 8,
        includeScore: true,
        maxResults: 25,
        threshold: 0.6,
    };

    useEffect(() => {
        if (fundingType === fundingTypeEnum.Lease) {
            if (
                registrationLink.business_lease &&
                !registrationLink.private_lease
            ) {
                dispatch(setLeaseType("business"));
                handleLeaseTypeClick("business");
            }
            if (
                registrationLink.private_lease &&
                !registrationLink.business_lease
            ) {
                dispatch(setLeaseType("private"));
                handleLeaseTypeClick("private");
            }
        }

        if (
            registrationLink.private_insurance &&
            !registrationLink.private_lease &&
            !registrationLink.business_lease
        ) {
            handleFundingTypeClick(fundingTypeEnum.Particulier);
        }
        if (
            !registrationLink.private_insurance &&
            (registrationLink.private_lease || registrationLink.business_lease)
        ) {
            handleFundingTypeClick(fundingTypeEnum.Lease);
        }
    }, [
        fundingType,
        registrationLink.business_lease,
        registrationLink.private_lease,
        registrationLink.private_insurance,
        dispatch,
        handleFundingTypeClick,
        handleLeaseTypeClick,
    ]);

    if (
        renderCondition &&
        typeof dossier.appointment_type !== "string" &&
        dossier?.appointment_type?.code !== dossierTypeEnum.CaravanRepair
    ) {
        return (
            <>
                {/** TEMP FIX */}
                {window.location.pathname !== "/athlon" &&
                    window.location.pathname !== "/unigarant" &&
                    (registrationLink.private_lease ||
                        registrationLink.business_lease) &&
                    registrationLink.private_insurance && (
                        <div
                            className="s-appointment__group border-color-primary"
                            ref={leaseOrParticulierRef}
                        >
                            <h1 className="s-appointment__group__title">
                                Lease of particulier
                            </h1>
                            <div className="c-form__buttons c-form__buttons--row">
                                <button
                                    type="button"
                                    onClick={() => {
                                        dispatch(setShowContactorNotice(false));
                                        dispatch(
                                            setLeaseActiveContractor(null),
                                        );
                                        dispatch(
                                            setInsuranceActiveContractor(null),
                                        );
                                        handleFundingTypeClick(
                                            fundingTypeEnum.Lease,
                                        );
                                    }}
                                    className={
                                        fundingType === fundingTypeEnum.Lease
                                            ? "c-form__buttons__single border-color-primary border-color-primary-hover button-active"
                                            : "c-form__buttons__single border-color-primary border-color-primary-hover"
                                    }
                                >
                                    <span className="font-face-primary">
                                        Lease
                                    </span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        dispatch(setShowContactorNotice(false));
                                        dispatch(
                                            setLeaseActiveContractor(null),
                                        );
                                        dispatch(
                                            setInsuranceActiveContractor(null),
                                        );
                                        handleFundingTypeClick(
                                            fundingTypeEnum.Particulier,
                                        );
                                    }}
                                    className={
                                        fundingType ===
                                        fundingTypeEnum.Particulier
                                            ? "c-form__buttons__single border-color-primary border-color-primary-hover button-active"
                                            : "c-form__buttons__single border-color-primary border-color-primary-hover"
                                    }
                                >
                                    <span className="font-face-primary">
                                        Particulier
                                    </span>
                                </button>

                                <p>
                                    Ik wil zelf de schade betalen klik{" "}
                                    <button
                                        type="button"
                                        onClick={() =>
                                            dispatch(
                                                setFundingType(
                                                    fundingTypeEnum.Self,
                                                ),
                                            )
                                        }
                                        style={{
                                            fontWeight: "bold",
                                            textDecoration: "underline",
                                            border: "none",
                                            background: "none",
                                        }}
                                    >
                                        hier
                                    </button>
                                </p>
                            </div>
                        </div>
                    )}

                {fundingType === fundingTypeEnum.Lease && contractors && (
                    <>
                        <div
                            className="s-appointment__group border-color-primary"
                            ref={leaseFollowUpRef}
                        >
                            <h1 className="s-appointment__group__title">
                                Is je leaseauto zakelijk of privé?
                            </h1>
                            <div className={"c-input__group"}>
                                <div className="c-form__buttons c-form__buttons--row c-form__buttons--no-margin">
                                    <button
                                        className={`c-form__buttons__single border-color-primary border-color-primary-hover ${vehicleUsage === BovagTermsTypeEnum.BUSINESS ? "button-active" : ""}`}
                                        type={"button"}
                                        onClick={() =>
                                            handleVehicleUsageChange(
                                                BovagTermsTypeEnum.BUSINESS,
                                            )
                                        }
                                    >
                                        <span className="font-face-primary">
                                            Zakelijk
                                        </span>
                                    </button>
                                    <button
                                        className={`c-form__buttons__single border-color-primary border-color-primary-hover ${vehicleUsage === BovagTermsTypeEnum.PRIVATE ? "button-active" : ""}`}
                                        type={"button"}
                                        onClick={() =>
                                            handleVehicleUsageChange(
                                                BovagTermsTypeEnum.PRIVATE,
                                            )
                                        }
                                    >
                                        <span className="font-face-primary">
                                            Privé
                                        </span>
                                    </button>
                                </div>
                                <p>
                                    Zo tonen we de juiste BOVAG-voorwaarden voor
                                    jouw situatie
                                </p>
                            </div>
                        </div>

                        {vehicleUsage && (
                            <>
                                <div
                                    className="s-appointment__group border-color-primary"
                                    ref={leaseContractorRef}
                                >
                                    <h1 className="s-appointment__group__title">
                                        Wat is de naam van jouw
                                        leasemaatschappij?
                                    </h1>
                                    <div className="c-form__select react-select">
                                        <FormSelectFuzzy
                                            fuzzyOptions={fuzzyOptions}
                                            wait={300}
                                            value={
                                                lease.activeContractorId ?? ""
                                            }
                                            options={contractors.map(
                                                (
                                                    insurance: ContractorInterface,
                                                ) => {
                                                    return {
                                                        value: insurance.id,
                                                        label: insurance.name,
                                                    };
                                                },
                                            )}
                                            onChange={(option) =>
                                                handleContractorSelect({
                                                    dispatch,
                                                    option: option,
                                                    contractors,
                                                    type: ContractorEnum.LEASE,
                                                })
                                            }
                                        />
                                        <p style={{ marginTop: "1rem" }}>
                                            Meld de schade ook direct bij de
                                            leasemaatschappij, zo kunnen we je
                                            sneller van dienst zijn
                                        </p>
                                    </div>
                                </div>

                                {/* Extern melden notice bij leasemaatschappij met redirect */}
                                {showContactorNotice && (
                                    <ContractorNotice
                                        slug={slug}
                                        contractors={contractors}
                                        activeContractorId={
                                            lease.activeContractorId ?? null
                                        }
                                        type={ContractorEnum.LEASE}
                                    />
                                )}

                                {!lease.activeContractorId &&
                                    !showContactorNotice && (
                                        <div className="s-appointment__group border-color-primary">
                                            <h1 className="s-appointment__group__title">
                                                Staat je leasemaatschappij er
                                                niet tussen?
                                            </h1>
                                            <div className="c-form__buttons c-form__buttons--row">
                                                <button
                                                    type="button"
                                                    onClick={
                                                        handleContinueWithoutContractor
                                                    }
                                                    className="c-form__buttons__single--full-width c-form__buttons__single border-color-primary border-color-primary-hover"
                                                >
                                                    <span className="font-face-primary">
                                                        Ga verder
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                {missingContractor &&
                                    !lease.activeContractorId &&
                                    !showContactorNotice && (
                                        <div
                                            className="s-appointment__group border-color-primary"
                                            ref={leaseMissingContractorRef}
                                        >
                                            <h1 className="s-appointment__group__title">
                                                Missende leasemaatschappij
                                            </h1>
                                            <div className="c-form__group c-form__group--row">
                                                <input
                                                    type="text"
                                                    value={
                                                        missingContractorName
                                                    }
                                                    onChange={
                                                        handleMissingContractorNameChange
                                                    }
                                                    placeholder="Naam maatschappij"
                                                />
                                            </div>
                                            <div className="c-form__group c-form__group--row">
                                                <button
                                                    type="button"
                                                    onClick={
                                                        handleMissingContractorSubmit
                                                    }
                                                    className="c-form__buttons__single--full-width c-form__buttons__single border-color-primary border-color-primary-hover"
                                                >
                                                    <span className="font-face-primary">
                                                        Ga verder
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                            </>
                        )}
                    </>
                )}
            </>
        );
    } else return null;
};

export default VehicleInformationLease;
